import React from 'react';
import { BookOpen } from 'lucide-react';
import { Card } from '@/shared/ui';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';
import type { LedgerTransaction } from '@/entities/ledger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface LedgerTableProps {
  transactions: LedgerTransaction[];
  isLoading: boolean;
  emptyMessage?: string;
}

// ---------------------------------------------------------------------------
// Skeleton row (loading state)
// ---------------------------------------------------------------------------
function SkeletonRow(): React.ReactElement {
  return (
    <tr aria-hidden="true">
      {[1, 2, 3, 4, 5].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-3.5 animate-pulse rounded bg-slate-100" />
        </td>
      ))}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Skeleton card (mobile loading state)
// ---------------------------------------------------------------------------
function SkeletonCard(): React.ReactElement {
  return (
    <Card className="animate-pulse space-y-2" aria-hidden="true">
      <div className="flex justify-between">
        <div className="h-3.5 w-24 rounded bg-slate-100" />
        <div className="h-3.5 w-16 rounded bg-slate-100" />
      </div>
      <div className="h-3 w-40 rounded bg-slate-100" />
      <div className="h-3 w-20 rounded bg-slate-100" />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Point display helper
// ---------------------------------------------------------------------------
function PointDisplay({ points }: { points: number }): React.ReactElement {
  const isPositive = points > 0;
  const isNegative = points < 0;

  return (
    <span
      className={cn(
        'font-semibold tabular-nums',
        isPositive && 'text-secondary-600',
        isNegative && 'text-danger-600',
        !isPositive && !isNegative && 'text-slate-600',
      )}
      aria-label={`${isPositive ? 'Earned' : isNegative ? 'Spent' : 'Adjusted'} ${Math.abs(points)} points`}
    >
      {isPositive ? '+' : ''}
      {points.toLocaleString()} pts
    </span>
  );
}

// ---------------------------------------------------------------------------
// Mobile card item
// ---------------------------------------------------------------------------
function TransactionCard({ tx }: { tx: LedgerTransaction }): React.ReactElement {
  return (
    <Card padding="sm" className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 truncate">{tx.referenceName}</p>
          <p className="text-xs text-slate-500 mt-0.5">{formatDate(tx.createdAt)}</p>
        </div>
        <PointDisplay points={tx.points} />
      </div>
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          Balance: <span className="font-medium text-slate-700">{tx.balanceAfter.toLocaleString()} pts</span>
        </span>
        {tx.approvedByName && (
          <span className="truncate max-w-[120px]">
            By: <span className="font-medium">{tx.approvedByName}</span>
          </span>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// LedgerTable component
// ---------------------------------------------------------------------------
export default function LedgerTable({
  transactions,
  isLoading,
  emptyMessage = 'No transactions found.',
}: LedgerTableProps): React.ReactElement {
  // ---- Loading state ----
  if (isLoading) {
    return (
      <>
        {/* Desktop skeleton */}
        <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-card">
          <table className="w-full text-sm" aria-label="Ledger transactions loading" aria-busy="true">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Points</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Balance After</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Approved By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {[1, 2, 3, 4, 5].map((i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
        {/* Mobile skeleton */}
        <div className="md:hidden space-y-3">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      </>
    );
  }

  // ---- Empty state ----
  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-slate-200 bg-white py-12 text-center shadow-card">
        <BookOpen size={36} className="text-slate-300" aria-hidden="true" />
        <p className="text-sm text-slate-500">{emptyMessage}</p>
      </div>
    );
  }

  // ---- Data ----
  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-card">
        <table className="w-full text-sm" aria-label="Ledger transactions">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Date
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Description
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Points
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Balance After
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Approved By
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {transactions.map((tx) => (
              <tr key={tx.id} className="hover:bg-slate-50/60 transition-colors duration-100">
                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">
                  {formatDate(tx.createdAt)}
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900 truncate max-w-[200px]">{tx.referenceName}</p>
                  {tx.note && (
                    <p className="text-xs text-slate-400 truncate max-w-[200px] mt-0.5">{tx.note}</p>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  <PointDisplay points={tx.points} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-slate-700 tabular-nums">
                  {tx.balanceAfter.toLocaleString()} pts
                </td>
                <td className="px-4 py-3 text-xs text-slate-500 truncate max-w-[120px]">
                  {tx.approvedByName || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3" aria-label="Ledger transactions">
        {transactions.map((tx) => (
          <TransactionCard key={tx.id} tx={tx} />
        ))}
      </div>
    </>
  );
}

